import React, { useCallback, useEffect, useState } from 'react';
import ImageViewer from 'react-simple-image-viewer';
import { hexToRgb, rgbDataURL } from 'corporate-utils';
import Image from 'next/image';

import { getStrapiMedia } from '../../utils/media';

import {
  ImageWrapper,
  LargeImageContainer,
  ExpandDiv,
  ImageViewerWrapper,
  ImgPreviewWrapper,
  StyledZoomInIcon,
  PreviewImg,
  StyledArrowDropUpIcon,
  StyledArrowDropDownIcon,
} from './ImageViewerFls.style';
import { useWindowSize } from 'usehooks-ts';

interface ImagesViewerLiftedStateProps {
  images: string[];
  prodName?: string;
  shouldBeSticky: boolean;
  handleShouldBeSticky: any;
}

const ImagesViewerLiftedState: React.FC<ImagesViewerLiftedStateProps> = ({
  images,
  prodName,
  shouldBeSticky,
  handleShouldBeSticky,
}: ImagesViewerLiftedStateProps) => {
  const { width } = useWindowSize();
  const [currentImage, setCurrentImage] = useState<number>(0);
  const [currentPreviewPage, setCurrentPreviewPage] = useState<number>(0);
  const [isViewerOpen, setIsViewerOpen] = useState<boolean>(false);

  const [imagesState, setImagesState] = useState<any[]>([]);

  const handleZoomMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.pointerType !== 'mouse') return;
      const img = e.currentTarget.querySelector<HTMLElement>('img');
      if (!img) return;
      const imgRect = img.getBoundingClientRect();
      const x = Math.max(
        0,
        Math.min(100, ((e.clientX - imgRect.left) / imgRect.width) * 100)
      );
      const y = Math.max(
        0,
        Math.min(100, ((e.clientY - imgRect.top) / imgRect.height) * 100)
      );
      img.style.transformOrigin = `${x}% ${y}%`;
    },
    []
  );

  const handleZoomEnter = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.pointerType !== 'mouse') return;
      const img = e.currentTarget.querySelector<HTMLElement>('img');
      if (!img) return;
      img.style.transform = 'scale(2.5)';
      img.style.cursor = 'zoom-in';
    },
    []
  );

  const handleZoomLeave = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.pointerType !== 'mouse') return;
      const img = e.currentTarget.querySelector<HTMLElement>('img');
      if (!img) return;
      img.style.transform = '';
      img.style.transformOrigin = '';
      e.currentTarget.style.cursor = '';
    },
    []
  );

  const [itemsPerPage, setItemsPerPage] = useState<number>(4);
  const sendToPreview = useCallback((index) => {
    setCurrentImage(index);
  }, []);

  const openImageViewer = (): void => {
    handleShouldBeSticky(!shouldBeSticky);
    setIsViewerOpen(!isViewerOpen);
  };

  const nextPreview = (): void => {
    setCurrentPreviewPage(currentPreviewPage + 1);
  };

  const prevPreview = (): void => {
    setCurrentPreviewPage(currentPreviewPage - 1);
  };

  useEffect(() => {
    setImagesState(images);
  }, [images]);

  useEffect(() => {
    width >= 900 ? setItemsPerPage(4) : setItemsPerPage(20);
  }, [width]);

  return (
    <>
      <ImageWrapper>
        <LargeImageContainer
          onPointerEnter={handleZoomEnter}
          onPointerLeave={handleZoomLeave}
          onPointerMove={handleZoomMove}
        >
          {imagesState?.[currentImage] && (
            <Image
              layout="fill"
              objectFit="contain"
              placeholder="blur"
              blurDataURL={rgbDataURL(
                hexToRgb('ffffff')?.[0],
                hexToRgb('ffffff')?.[1],
                hexToRgb('ffffff')?.[2]
              )}
              src={getStrapiMedia(imagesState[currentImage] || '')}
              alt={prodName || ''}
              onClick={openImageViewer}
              onError={() => {
                const copyImageState = [...imagesState];
                copyImageState.splice(currentImage, 1);
                setImagesState(copyImageState);
              }}
            />
          )}
          <ExpandDiv>
            <StyledZoomInIcon />
          </ExpandDiv>
        </LargeImageContainer>
        <ImgPreviewWrapper>
          <StyledArrowDropUpIcon
            onClick={currentPreviewPage !== 0 ? prevPreview : () => {}}
            disabled={currentPreviewPage === 0}
            shouldRotate={width >= 900 ? false : true}
            display={width >= 900 || currentPreviewPage !== 0 ? true : false}
          />
          {imagesState
            ?.map((_, index) => (
              <PreviewImg key={index} isActive={index === currentImage}>
                {imagesState[currentImage] && (
                  <Image
                    layout="fill"
                    objectFit="contain"
                    placeholder="blur"
                    blurDataURL={rgbDataURL(
                      hexToRgb('ffffff')?.[0],
                      hexToRgb('ffffff')?.[1],
                      hexToRgb('ffffff')?.[2]
                    )}
                    src={getStrapiMedia(imagesState[index] || '')}
                    alt={prodName || ''}
                    onClick={() => sendToPreview(index)}
                    onError={() => {
                      const copyImageState = [...imagesState];
                      copyImageState.splice(index, 1);
                      setImagesState(copyImageState);
                    }}
                  />
                )}
              </PreviewImg>
            ))
            .slice(
              currentPreviewPage * itemsPerPage,
              (currentPreviewPage + 1) * itemsPerPage > imagesState.length
                ? imagesState?.length
                : (currentPreviewPage + 1) * itemsPerPage
            )}
          <StyledArrowDropDownIcon
            onClick={
              currentPreviewPage !==
              Math.floor((imagesState?.length - 1) / itemsPerPage)
                ? nextPreview
                : () => {}
            }
            disabled={
              currentPreviewPage ===
              Math.floor((imagesState?.length - 1) / itemsPerPage)
            }
            shouldRotate={width >= 900 ? false : true}
            display={
              width >= 900 ||
              currentPreviewPage !==
                Math.floor((imagesState?.length - 1) / itemsPerPage)
                ? true
                : false
            }
          />
        </ImgPreviewWrapper>
      </ImageWrapper>

      {isViewerOpen && (
        <ImageViewerWrapper>
          <ImageViewer
            src={imagesState.map((url: string) => getStrapiMedia(url))}
            currentIndex={currentImage}
            onClose={openImageViewer}
            disableScroll={false}
            closeOnClickOutside={true}
          />
        </ImageViewerWrapper>
      )}
    </>
  );
};

export default ImagesViewerLiftedState;
